import * as Phaser from 'phaser';
import { WeaponSystem } from '../systems/WeaponSystem';
import { UpgradeSystem } from '../systems/UpgradeSystem';
import { PlayerStats } from '../components/PlayerStats';
import { LocalizationManager } from '../managers/LocalizationManager';
import { EventBus } from '../core/EventBus';
import { VolumeSlider } from './VolumeSlider';
import { AudioManager } from '../managers/AudioManager';
import { ProfileRuntime, getSharedProfileRuntime } from '../managers/ProfileRuntime';
import { WEAPON_ICONS } from '../data/assets/visualAssets';
import { createTelegramLink } from './TelegramLink';
import { UITheme } from './UITheme';
import { UIButton, bindUIFocus } from './UIButton';

export class PauseScreen extends Phaser.GameObjects.Container {
    private telegramLink: Phaser.GameObjects.Text;
    private background: Phaser.GameObjects.Rectangle;
    private titleText: Phaser.GameObjects.Text;
    private controlsLayer: Phaser.GameObjects.Container;
    private weaponsList: Phaser.GameObjects.Container;
    private passivesList: Phaser.GameObjects.Container;
    private statsText: Phaser.GameObjects.Text;
    private musicLabel: Phaser.GameObjects.Text;
    private sfxLabel: Phaser.GameObjects.Text;
    private musicVolSlider: VolumeSlider;
    private sfxVolSlider: VolumeSlider;
    private muteButton: UIButton;
    private button: UIButton;
    private tabs: UIButton[];
    private effectButtons: UIButton[];
    private tab = 0;
    private previousTopOnly: boolean | null = null;
    private releaseFocus?: () => void;
    private inventory: Array<Array<{icon: string; name: string; level: number; maxLevel: number}>> = [[], []];

    constructor(scene: Phaser.Scene, private readonly audioManager: AudioManager, private readonly profileRuntime: ProfileRuntime = getSharedProfileRuntime()) {
        super(scene, 0, 0);
        const loc = LocalizationManager.getInstance();
        this.setDepth(3000).setScrollFactor(0).setVisible(false);
        this.background = scene.add.rectangle(0, 0, scene.scale.width, scene.scale.height, UITheme.background, 0.97).setOrigin(0).setInteractive();
        this.background.on('pointerdown', (_p: unknown, _x: number, _y: number, e?: Phaser.Types.Input.EventData) => e?.stopPropagation());
        this.titleText = scene.add.text(0, 0, loc.getText('ui.paused'), {fontFamily: UITheme.heading, fontSize:'28px', color:UITheme.text}).setOrigin(0.5);
        this.weaponsList = scene.add.container(0, 0);
        this.passivesList = scene.add.container(0, 0);
        this.statsText = scene.add.text(0, 0, '', {fontFamily:UITheme.body,fontSize:'18px',color:UITheme.text,align:'center'}).setOrigin(0.5);
        this.add([this.background, this.titleText, this.weaponsList, this.passivesList, this.statsText]);
        this.telegramLink = createTelegramLink(scene).setScrollFactor(0);
        this.add(this.telegramLink);
        this.controlsLayer = scene.add.container(0, 0).setScrollFactor(0);
        this.add(this.controlsLayer);
        this.tabs = ['ui.inventory', 'ui.stats', 'ui.sound', 'ui.effects'].map((key,index) => new UIButton(scene, 0, 0, loc.getText(key), () => {this.tab=index;this.resize();this.focusControls();}, 140, 44));
        this.effectButtons = ['reduceShake', 'reduceFlashes'].map(key => {
            let enabled = this.profileRuntime.loadProfile().settings.gameplay[key] === true;
            const label = () => `${loc.getText(`ui.${key}`)}: ${loc.getText(enabled ? 'ui.enabled' : 'ui.disabled')}`;
            const button = new UIButton(scene, 0, 0, label(), () => {
                const result = this.profileRuntime.updateSettings({ gameplay: { [key]: !enabled } });
                enabled = result.profile.settings.gameplay[key] === true;
                button.setText(label());
            }, 280, 48);
            return button;
        });
        this.button = new UIButton(scene, 0, 0, loc.getText('ui.resume'), () => this.hide());
        this.muteButton = new UIButton(scene, 0, 0, '', () => {
            this.audioManager.setMute(!this.audioManager.getMute());
            this.refreshMute();
        }, 180, 44);
        this.musicVolSlider = new VolumeSlider(scene, 0, 0, 180, audioManager.getMusicVolume(), value => audioManager.setMusicVolume(value));
        this.sfxVolSlider = new VolumeSlider(scene, 0, 0, 180, audioManager.getSfxVolume(), value => audioManager.setSfxVolume(value));
        this.musicLabel = scene.add.text(0, 0, loc.getText('ui.sound.music'), {fontFamily:UITheme.body,fontSize:'18px',color:UITheme.text}).setOrigin(0.5);
        this.sfxLabel = scene.add.text(0, 0, loc.getText('ui.sound.sfx'), {fontFamily:UITheme.body,fontSize:'18px',color:UITheme.text}).setOrigin(0.5);
        this.controlsLayer.add([...this.tabs, ...this.effectButtons, this.button, this.muteButton, this.musicVolSlider, this.sfxVolSlider, this.musicLabel, this.sfxLabel]);
        scene.add.existing(this);
        this.resize();
    }
    public show(weaponSystem: WeaponSystem, upgradeSystem: UpgradeSystem, stats?: PlayerStats): void {
        if (this.previousTopOnly === null) this.previousTopOnly = this.scene.input.topOnly;
        this.scene.input.setTopOnly(true);
        const loc = LocalizationManager.getInstance();
        this.inventory = [weaponSystem.getActiveWeapons().map(w => ({icon:WEAPON_ICONS[w.id] ?? w.icon,name:loc.getText(w.nameKey ?? `weapon.${w.id}.name`),level:w.level,maxLevel:w.maxLevel})), upgradeSystem.getInventorySnapshot().passives.map(p => ({icon:p.icon,name:loc.getText(p.nameKey),level:p.level,maxLevel:p.maxLevel}))];
        const lines: string[] = [];
        if (stats) for (const [key,value] of [
            ['ui.damage', `${Math.round(stats.get('damageMultiplier') * 100)}%`],
            ['ui.cooldown', `${Math.round(stats.get('cooldownMultiplier') * 100)}%`],
            ['ui.speed', stats.get('moveSpeed')], ['ui.pickup', stats.get('pickupRange')], ['ui.max_hp', stats.get('maxHealth')]
        ]) lines.push(`${loc.getText(String(key))}: ${value}`);
        const records = this.profileRuntime.getProfileSnapshot().profile?.records;
        if(records) lines.push(loc.getText('profile.records.best_time',[Math.floor(records.bestSurvivedSeconds)]),loc.getText('profile.records.runs',[records.totalRuns,records.victories]));
        this.statsText.setText(lines.join('\n'));
        this.musicVolSlider.setValue(this.audioManager.getMusicVolume());
        this.sfxVolSlider.setValue(this.audioManager.getSfxVolume());
        this.refreshMute();
        this.tab = 0;
        this.setVisible(true);
        this.resize();
        this.focusControls();
    }
    private refreshMute(): void {
        this.muteButton.setText(LocalizationManager.getInstance().getText(this.audioManager.getMute() ? 'ui.sound.mute_on' : 'ui.sound.mute_off'));
    }
    private focusControls(): void {
        this.releaseFocus?.();
        this.releaseFocus = bindUIFocus(this.scene, [...this.tabs, ...(this.tab===3 ? this.effectButtons : []), ...(this.tab===2 ? [this.muteButton,this.musicVolSlider,this.sfxVolSlider] : []), this.button, ...(this.tab===1 ? [{setFocused: (focused: boolean) => this.telegramLink.setStyle({color: focused ? "#ecc778" : UITheme.text}), activate: () => this.telegramLink.emit("pointerup", null,0,0,{stopPropagation(){}})}] : [])]);
    }
    public resize(): void {
        const {width,height} = this.scene.scale;
        this.background.setSize(width,height);
        this.titleText.setPosition(width/2,26);
        const tabWidth = Math.min(140,(width-40)/4);
        this.tabs.forEach((b,i) => {b.setPosition(width/2+(i-1.5)*(tabWidth+4),76);b.setScale(Math.min(1,tabWidth/140),1);});
        this.effectButtons.forEach((button, i) => button.setPosition(width/2, height/2 - 20 + i*60).setVisible(this.tab===3));
        this.button.setPosition(width/2,height-30);
        const columnWidth = Math.min(400,(width-48)/2);
        this.weaponsList.setPosition(width/2-columnWidth-8,108).setVisible(this.tab===0);
        this.passivesList.setPosition(width/2+8,108).setVisible(this.tab===0);
        [this.weaponsList,this.passivesList].forEach((section,i)=>this.renderRows(section,this.inventory[i] ?? [],columnWidth,i));
        this.telegramLink.setPosition(width/2,height-78).setVisible(this.tab===1);
        this.statsText.setOrigin(0.5,0).setPosition(width/2,108).setVisible(this.tab===1);
        const soundY = Math.max(158,height/2);
        this.muteButton.setPosition(width/2,soundY+72).setVisible(this.tab===2);
        this.musicLabel.setPosition(width/2-130,soundY-64).setVisible(this.tab===2);
        this.sfxLabel.setPosition(width/2+130,soundY-64).setVisible(this.tab===2);
        this.musicVolSlider.setPosition(width/2-130,soundY).setVisible(this.tab===2);
        this.sfxVolSlider.setPosition(width/2+130,soundY).setVisible(this.tab===2);
    }
    private renderRows(section: Phaser.GameObjects.Container, items: typeof this.inventory[number], width: number, index: number): void {
        section.removeAll(true);
        const loc=LocalizationManager.getInstance();
        if(this.scene.textures.exists(index===0?'ui_weapons':'ui_equipment')) section.add(this.scene.add.image(12,10,index===0?'ui_weapons':'ui_equipment').setDisplaySize(24,24));
        section.add(this.scene.add.text(30,0,loc.getText(index===0?'ui.weapons':'ui.passives'),{fontFamily:UITheme.body,fontSize:'16px',color:'#ecc778'}));
        if(!items.length) section.add(this.scene.add.text(0,30,loc.getText('ui.none'),{fontFamily:UITheme.body,fontSize:'16px',color:UITheme.muted}));
        items.slice(0,6).forEach((item,i)=>{
            const y=32+i*28;
            const bg=this.scene.add.rectangle(0,y,width,26,UITheme.panel).setOrigin(0,0.5);
            section.add(bg);
            if(this.scene.textures.exists(item.icon)) section.add(this.scene.add.image(13,y,item.icon).setDisplaySize(24,24));
            section.add(this.scene.add.text(30,y,item.name,{fontFamily:UITheme.body,fontSize:'16px',color:UITheme.text}).setOrigin(0,0.5));
            section.add(this.scene.add.text(width-4,y,`${item.level}/${item.maxLevel}`,{fontFamily:UITheme.body,fontSize:'16px',color:UITheme.muted}).setOrigin(1,0.5));
        });
    }
    public hide(): void {
        if(!this.visible) return;
        this.setVisible(false);
        this.releaseFocus?.(); this.releaseFocus=undefined;
        if(this.previousTopOnly!==null){this.scene.input.setTopOnly(this.previousTopOnly);this.previousTopOnly=null;}
        EventBus.getInstance().publish({type:'game:resume'});
    }
}
